<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, onMounted, watch, nextTick, inject } from "vue"
import { useToast } from "primevue/usetoast"
import { useI18n } from "vue-i18n"
import { normalizeString } from "@core/helpers"
import type { BuildingVisibleToEntry, ContactWithAccess } from "@types"
import { Services } from "@services/register"
import { useFetchWrapper } from "@core/composables"
import { ServiceError } from "@core/exceptions"

defineOptions({
    name: "AccessFolderListUser",
})

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            common: {
                success: "Succès",
                error: "Erreur",
                retry: "Réessayer",
                cancel: "Annuler",
                validate: "Valider",
            },
            access: {
                placeholders: {
                    search: "Rechercher par nom, prénom ou email...",
                    sort: "Trier les contacts",
                },
                stats: {
                    collaborators:
                        "{count} collaborateur • {count} avec accès | {count} collaborateurs • {count_access} avec accès",
                    modifications:
                        "{count} modification en attente | {count} modifications en attente",
                },
                selectAll: {
                    selectAll: "Tout sélectionner",
                    deselectAll: "Tout désélectionner",
                },
                empty: {
                    noResults: "Aucun contact trouvé",
                    noCollaborators: "Aucun collaborateur à afficher",
                },
                sort: {
                    label: "Trier par :",
                    firstname_asc: "Prénom (A-Z)",
                    firstname_desc: "Prénom (Z-A)",
                    lastname_asc: "Nom (A-Z)",
                    lastname_desc: "Nom (Z-A)",
                    email_asc: "Email (A-Z)",
                    email_desc: "Email (Z-A)",
                },
                errors: {
                    noFolderSelected: "Aucun dossier sélectionné",
                    loadingContacts:
                        "Erreur lors du chargement des contacts. Tous les changements ont été annulés.",
                    cannotLoadContacts:
                        "Impossible de charger la liste des contacts",
                    updatingAccess:
                        "Erreur lors de la mise à jour des accès. Tous les changements ont été annulés.",
                },
                messages: {
                    accessUpdatedSuccess:
                        "Les accès ont été mis à jour avec succès",
                },
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================

/**
 * Type pour tracker les changements d'accès localement
 */
type AccessChange = {
    _id: string
    access: boolean
}

// ================================
// PROPS
// ================================

interface Props {
    /**
     * Identifiant du dossier dont on gère les accès.
     */
    folderId: string
    /**
     * Hauteur du virtual scroller (liste des contacts).
     */
    listHeight?: string
}

const props = withDefaults(defineProps<Props>(), {
    listHeight: "600px",
})

const emit = defineEmits<{
    /**
     * Après enregistrement réussi des accès (liste à jour pour le store / UI).
     */
    "access-updated": [
        payload: {
            folderId: string
            visibleTo: BuildingVisibleToEntry[]
        },
    ]
}>()

// ================================
// COMPOSABLES & STORES
// ================================

/**
 * Service de gestion des toasts
 */
const toast = useToast()

/**
 * Injection des services
 */
const {
    $getContactsWithAccessForFolder,
    $addFolderAccess,
    $deleteFolderAccess,
} = inject<Services>("services")!

// ================================
// ÉTAT DES DONNÉES
// ================================

/**
 * Liste complète des contacts avec leurs accès
 */
const contacts = ref<ContactWithAccess[]>([])

/**
 * Copie de sauvegarde des contacts pour le rollback
 */
const originalContacts = ref<ContactWithAccess[]>([])

/**
 * Liste des modifications d'accès en attente
 */
const accessChanges = ref<AccessChange[]>([])

/**
 * Terme de recherche pour filtrer les contacts
 */
const searchTerm = ref<string>("")

/**
 * Configuration du tri (champ et direction)
 */
const sortConfig = ref<{
    field: "firstname" | "lastname" | "username"
    direction: "asc" | "desc"
}>({
    field: "firstname",
    direction: "asc",
})

// ================================
// DONNÉES CALCULÉES
// ================================

/**
 * Options de tri disponibles pour l'utilisateur
 */
const getSortOptions = computed(() => [
    {
        label: `${t("access.sort.firstname_asc")} ↑`,
        value: { field: "firstname", direction: "asc" },
    },
    {
        label: `${t("access.sort.firstname_desc")} ↓`,
        value: { field: "firstname", direction: "desc" },
    },
    {
        label: `${t("access.sort.lastname_asc")} ↑`,
        value: { field: "lastname", direction: "asc" },
    },
    {
        label: `${t("access.sort.lastname_desc")} ↓`,
        value: { field: "lastname", direction: "desc" },
    },
    {
        label: `${t("access.sort.email_asc")} ↑`,
        value: { field: "username", direction: "asc" },
    },
    {
        label: `${t("access.sort.email_desc")} ↓`,
        value: { field: "username", direction: "desc" },
    },
])

/**
 * Liste des contacts filtrés par la recherche
 */
const getFilteredContacts = computed(() => {
    const search = normalizeString(searchTerm.value.toLowerCase())

    return contacts.value.filter((contact) => {
        const firstname = normalizeString(
            contact.firstname?.toLowerCase() || ""
        )
        const lastname = normalizeString(contact.lastname?.toLowerCase() || "")
        const email = normalizeString(contact.username?.toLowerCase() || "")

        return (
            firstname.includes(search) ||
            lastname.includes(search) ||
            email.includes(search)
        )
    })
})

/**
 * Liste des contacts filtrés et triés
 */
const getSortedContacts = computed(() => {
    const sorted = [...getFilteredContacts.value]

    sorted.sort((a, b) => {
        const aValue = normalizeString(a[sortConfig.value.field] || "")
        const bValue = normalizeString(b[sortConfig.value.field] || "")

        const comparison = aValue.localeCompare(bValue)
        return sortConfig.value.direction === "asc" ? comparison : -comparison
    })

    return sorted
})

/**
 * Nombre de contacts avec accès activé
 */
const getContactsWithAccess = computed(() => {
    return contacts.value.filter((contact) => contact.access).length
})

/**
 * Indique s'il y a des modifications en attente
 */
const getHasChanges = computed(() => {
    return accessChanges.value.length > 0
})

/**
 * Tous les contacts visibles sont-ils sélectionnés ?
 */
const getAllVisible = computed({
    get: () => {
        if (getFilteredContacts.value.length === 0) return false
        return getFilteredContacts.value.every(
            (contact: ContactWithAccess) => contact.access
        )
    },
    set: (value: boolean) => {
        getFilteredContacts.value.forEach((contact: ContactWithAccess) => {
            if (contact.access !== value) {
                toggleAccess(contact._id, value)
            }
        })
    },
})

// ================================
// CHARGEMENT DES DONNÉES
// ================================

/**
 * Construit `visible_to` à partir des contacts ayant l’accès activé.
 */
function contactsWithAccessToVisibleTo(
    list: ContactWithAccess[]
): BuildingVisibleToEntry[] {
    return list
        .filter((c) => c.access)
        .map((c) => ({
            _id_user: c._id,
            _id: c._id,
            firstname: c.firstname,
            lastname: c.lastname,
            username: c.username,
            img: c.img ?? null,
        }))
}

/**
 * Charge la liste des contacts avec leurs accès depuis l'API
 */
const {
    fetch: fetchContacts,
    isFetching: isFetchingContacts,
    hasError: hasErrorContacts,
    error: errorContacts,
} = useFetchWrapper(async () => {
    if (!props.folderId) {
        throw new ServiceError({
            message: t("access.errors.noFolderSelected"),
            code: "UNKNOWN",
        })
    }
    try {
        const { data } = await $getContactsWithAccessForFolder(props.folderId)
        contacts.value = data
        originalContacts.value = JSON.parse(JSON.stringify(data))
        accessChanges.value = []
    } catch (err: unknown) {
        toast.add({
            severity: "error",
            summary: t("common.error"),
            detail: t("access.errors.cannotLoadContacts"),
            life: 5000,
        })
        throw new ServiceError({
            message: t("access.errors.loadingContacts"),
            code: "UNKNOWN",
            technicalMessage: err instanceof Error ? err.message : String(err),
        })
    }
})

// ================================
// GESTION DES ACCÈS
// ================================

/**
 * Active ou désactive l'accès pour un contact
 * @param contactId - Identifiant du contact
 * @param access - Nouvel état de l'accès (true = accès activé, false = désactivé)
 */
function toggleAccess(contactId: string, access: boolean): void {
    if (isFetchingSubmit.value) return

    const contactIndex = contacts.value.findIndex((c) => c._id === contactId)
    if (contactIndex !== -1) {
        contacts.value[contactIndex].access = access
    }
    const changeIndex = accessChanges.value.findIndex(
        (c) => c._id === contactId
    )
    if (changeIndex !== -1) {
        accessChanges.value[changeIndex].access = access
    } else {
        const newChange = { _id: contactId, access }
        accessChanges.value.push(newChange)
    }

    const originalContact = originalContacts.value.find(
        (c) => c._id === contactId
    )
    if (originalContact && originalContact.access === access) {
        accessChanges.value = accessChanges.value.filter(
            (c: AccessChange) => c._id !== contactId
        )
    }
    errorContacts.value = null
    errorSubmit.value = null
}

// ================================
// SAUVEGARDE ET ANNULATION
// ================================

const {
    fetch: submitChanges,
    isFetching: isFetchingSubmit,
    hasError: hasErrorSubmit,
    error: errorSubmit,
} = useFetchWrapper(async () => {
    if (!props.folderId || accessChanges.value.length === 0) return

    try {
        const toAdd = accessChanges.value
            .filter((change: AccessChange) => change.access)
            .map((change: AccessChange) => change._id)

        const toRemove = accessChanges.value
            .filter((change: AccessChange) => !change.access)
            .map((change: AccessChange) => change._id)

        const promises = []
        if (toAdd.length > 0) {
            promises.push(
                $addFolderAccess({
                    folderId: props.folderId,
                    userIds: toAdd,
                })
            )
        }
        if (toRemove.length > 0) {
            promises.push(
                $deleteFolderAccess({
                    folderId: props.folderId,
                    userIds: toRemove,
                })
            )
        }

        await Promise.all(promises)

        originalContacts.value = JSON.parse(JSON.stringify(contacts.value))
        accessChanges.value = []

        emit("access-updated", {
            folderId: props.folderId,
            visibleTo: contactsWithAccessToVisibleTo(contacts.value),
        })

        toast.add({
            severity: "success",
            summary: t("common.success"),
            detail: t("access.messages.accessUpdatedSuccess"),
            life: 3000,
        })
    } catch (err: unknown) {
        await rollbackChanges()

        toast.add({
            severity: "error",
            summary: t("common.error"),
            detail: t("access.errors.updatingAccess"),
            life: 5000,
        })
        throw new ServiceError({
            message: t("access.errors.updatingAccess"),
            code: "UNKNOWN",
            technicalMessage: err instanceof Error ? err.message : String(err),
        })
    }
})

/**
 * Annule toutes les modifications en attente et restaure l'état initial
 */
async function cancelChanges(): Promise<void> {
    await rollbackChanges()
}

/**
 * Effectue un rollback complet des modifications vers l'état original
 */
async function rollbackChanges(): Promise<void> {
    contacts.value = JSON.parse(JSON.stringify(originalContacts.value))
    accessChanges.value = []
    await nextTick()
}

// ================================
// INTERACTIONS UTILISATEUR
// ================================

/**
 * Gère le clic sur un contact pour basculer son accès
 * @param contact - Contact cliqué
 */
function onContactClick(contact: ContactWithAccess): void {
    if (isFetchingSubmit.value) return
    toggleAccess(contact._id, !contact.access)
}

// ================================
// CYCLE DE VIE ET WATCHERS
// ================================

onMounted(() => {
    if (props.folderId) {
        fetchContacts()
    }
})

watch(
    () => props.folderId,
    (newFolderId) => {
        if (newFolderId) {
            fetchContacts()
        }
    }
)
</script>

<template>
    <div class="access-folder-list-user flex flex-col gap-6 max-w-[800px]">
        <!-- ================================ -->
        <!-- BARRE DE RECHERCHE -->
        <!-- ================================ -->
        <BaseInputWrapper
            input-id="search-input-folder-access"
            :label="t('access.placeholders.search')"
        >
            <template #prefix>
                <BaseInputGroupAddon>
                    <i class="pi pi-search"></i>
                </BaseInputGroupAddon>
            </template>
            <BaseInputText v-model="searchTerm" class="w-full pl-10 pr-10">
            </BaseInputText>
        </BaseInputWrapper>

        <!-- ================================ -->
        <!-- ACTIONS ET TRI -->
        <!-- ================================ -->
        <div class="flex justify-between items-end flex-wrap gap-4">
            <div>
                <span class="text-sm text-gray-600">
                    {{
                        t("access.stats.collaborators", {
                            count: contacts.length,
                            count_access: getContactsWithAccess,
                        })
                    }}
                </span>
            </div>
            <div class="flex flex-col gap-2 min-w-[220px]">
                <label class="block mb-1 text-sm font-medium text-gray-700">
                    {{ t("access.sort.label") }}
                </label>
                <BaseSelect
                    v-model="sortConfig"
                    :options="getSortOptions"
                    option-label="label"
                    option-value="value"
                    class="min-w-[200px]"
                />
            </div>
        </div>

        <!-- ================================ -->
        <!-- LISTE DES CONTACTS -->
        <!-- ================================ -->
        <div class="border border-gray-200 rounded-b-lg bg-white">
            <div
                class="flex justify-between items-center px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg"
            >
                <div class="flex items-center gap-2">
                    <BaseCheckbox
                        v-model="getAllVisible"
                        binary
                        :disabled="
                            getFilteredContacts.length === 0 || isFetchingSubmit
                        "
                    />
                    <span class="text-sm font-medium">
                        {{
                            getAllVisible
                                ? t("access.selectAll.deselectAll")
                                : t("access.selectAll.selectAll")
                        }}
                    </span>
                </div>
            </div>
            <div v-if="isFetchingContacts" class="p-4">
                <BaseSkeleton
                    v-for="i in 6"
                    :key="i"
                    height="60px"
                    class="mb-2"
                />
            </div>

            <div
                v-else-if="
                    getFilteredContacts.length === 0 && !hasErrorContacts
                "
                class="flex flex-col items-center justify-center p-12 text-center"
            >
                <i class="pi pi-users text-4xl text-gray-400 mb-4"></i>
                <p class="text-gray-600 text-center">
                    {{
                        searchTerm
                            ? t("access.empty.noResults")
                            : t("access.empty.noCollaborators")
                    }}
                </p>
            </div>

            <BaseVirtualScroller
                v-else
                :items="getSortedContacts"
                :item-size="60"
                class="w-full"
                :style="{ height: listHeight }"
            >
                <template #item="{ item: contact }">
                    <div
                        :key="contact._id"
                        class="px-4 py-3 border-b border-gray-100 cursor-pointer transition-colors min-h-[60px] hover:bg-gray-50"
                        :class="{
                            'opacity-60 cursor-not-allowed': contact.disabled,
                        }"
                        @click="onContactClick(contact)"
                    >
                        <ItemUser
                            :user="contact"
                            :user-type="contact.type"
                            checkbox
                            :selected="contact.access"
                            :disabled="contact.disabled || isFetchingSubmit"
                            hide-phone
                            @update:selected="toggleAccess(contact._id, $event)"
                        />
                    </div>
                </template>
            </BaseVirtualScroller>
        </div>

        <div
            v-if="getHasChanges"
            class="flex justify-between items-center p-4 bg-gray-50 rounded-lg border border-gray-200"
        >
            <div>
                <span class="text-sm text-gray-600">
                    {{
                        t("access.stats.modifications", {
                            count: accessChanges.length,
                        })
                    }}
                </span>
            </div>
            <div class="flex gap-3">
                <BaseButton
                    :label="t('common.cancel')"
                    severity="primary"
                    outlined
                    :disabled="isFetchingSubmit"
                    @click="cancelChanges"
                />
                <BaseButton
                    :label="t('common.validate')"
                    severity="primary"
                    :loading="isFetchingSubmit || isFetchingContacts"
                    @click="submitChanges"
                />
            </div>
        </div>
        <BaseMessage v-if="hasErrorContacts" severity="error">
            {{ errorContacts?.message }}
        </BaseMessage>
        <BaseMessage v-if="hasErrorSubmit" severity="error">
            {{ errorSubmit?.message }}
        </BaseMessage>
    </div>
</template>
